import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import RedditPixel from 'react-reddit-pixel';
import { useNavigate } from 'react-router-dom';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { currencyMainUnitsFormatter } from 'app/shared/utils/currencyFormatter';
import { AuthContext } from 'app/shared/context/Auth';
import {
  Box,
  Divider,
  Typography,
  VisaLogo,
} from 'app/shared/components/atoms';
import { Checkbox } from 'app/shared/components/atoms';
import { ActionButton } from 'app/shared/components/molecules';
import {
  includeBookingFeeInDisplayPrice,
  useBookingFeeMessage,
} from 'app/fan/utils/bookingFee';
import {
  calcMaxTicketsCanPurchase,
  shouldDisplayAdditionalPurchaseConfirmationModal,
  ShowVirtualPayment,
  useCheckoutRedirect,
  usePriceBreakdown,
} from 'app/fan/utils/checkout';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { useExpressCheckoutContext } from 'app/fan/context/ExpressCheckout';
import { TicketQuantityStepper } from 'app/fan/components/molecules';
import {
  AdditionalPurchaseConfirmationModal,
  VirtualWalletButtons,
  VisaBenefitRemovalConfirmationModal,
} from 'app/fan/components/organisms';
import { TermsAndConditionsSummaryRow } from 'app/fan/components/pages/Event/components';
import {
  calcEventDisplayPrice,
  useVisaPromo,
} from 'app/fan/components/pages/Event/eventUtils';

import { PromoCodeLink } from './PromoCodeLink';
import TicketPrice from './TicketPrice';

export interface CheckoutWidgetProps {
  alreadyConfirmedAdditionalPurchase?: boolean;
  allowPromoCode?: boolean;
  disabled?: boolean;
}
export const CheckoutWidget: React.FC<
  React.PropsWithChildren<CheckoutWidgetProps>
> = ({
  alreadyConfirmedAdditionalPurchase = false,
  allowPromoCode = true,
  disabled = false,
}) => {
  const intl = useIntl();
  const { loggedIn, user } = useContext(AuthContext);
  const [
    displayAdditionalPurchaseConfirmationModal,
    setDisplayAdditionalPurchaseConfirmationModal,
  ] = useState(false);
  const {
    numberOfTickets,
    setNumberOfTickets,
    event,
    eventAttendee,
    promoCodeValue: { promoCodeWithCharges, minimumNumberOfTickets },
    optinGuestMarketing,
    setOptinGuestMarketing,
    stripeVirtualWallet,
  } = useExpressCheckoutContext();

  const {
    canRedeemVisaFreeTicket,
    userIsVisaEligible,
    canBypassVisaValidation,
  } = useVisaPromo({ event });

  const { setShouldDisplayVisaDisclaimer } = useEventPageContext();

  const [visaPromoApplied, setVisaPromoApplied] = useState(false);

  const { colors } = useCurrentTheme();

  useEffect(() => {
    if (canRedeemVisaFreeTicket) {
      setVisaPromoApplied(true);
    } else {
      setVisaPromoApplied(false);
    }

    return () => {
      setVisaPromoApplied(false);
    };
  }, [canRedeemVisaFreeTicket]);

  const canRedeemAndIsRedeemingVisaFreeTicket =
    visaPromoApplied && numberOfTickets > 1;
  const numberOfVisaFreeTickets = canRedeemAndIsRedeemingVisaFreeTicket ? 1 : 0;

  const feeIncludedInDisplayPrice = includeBookingFeeInDisplayPrice(event);
  const bookingFeeMessage = useBookingFeeMessage({
    event,
    userIsVisaEligible,
    canBypassVisaValidation,
  });

  const { finalPrice, currencySymbol, promoCodeDiscountAmount } =
    usePriceBreakdown({
      numberOfTickets,
      promoCodeWithCharges,
      numberOfVisaFreeTickets,
      canRedeemAndIsRedeemingVisaFreeTicket,
      excludeFeesFromCalculation: !feeIncludedInDisplayPrice,
      event,
      eventAttendee: eventAttendee || null,
    });
  const navigate = useNavigate();
  const { checkoutUrlForEvent } = useCheckoutRedirect(event);
  const [confirmedAdditionalPurchase, setConfirmedAdditionalPurchase] =
    useState(alreadyConfirmedAdditionalPurchase);
  const [
    displaySingleTicketDeclineVisaFreeTicketConfirmationModal,
    setDisplaySingleTicketDeclineVisaFreeTicketConfirmationModal,
  ] = useState(false);
  const { trackAnalyticsEvent } = useAnalyticsContext();

  function navigateToCheckout() {
    RedditPixel.init('a2_ekhq3a2jqrod');
    RedditPixel.track('AddToCart');
    if (
      shouldDisplayAdditionalPurchaseConfirmationModal(user?.id, event.id) &&
      !confirmedAdditionalPurchase
    ) {
      trackAnalyticsEvent('Purchase Confirmation Modal Displayed', {});
      setDisplayAdditionalPurchaseConfirmationModal(true);
      setConfirmedAdditionalPurchase(true);
    } else {
      const code = promoCodeWithCharges?.promoCode?.code || '';
      navigate(
        `${checkoutUrlForEvent}?promo_code=${code}&ticket_quantity=${numberOfTickets}`
      );
    }
  }

  if (!event) {
    return null;
  }

  const ticketPrice = calcEventDisplayPrice(event, feeIncludedInDisplayPrice);

  const PromoCodeSummaryItem = () => {
    const isRedeemingVisaFreeTicket = numberOfVisaFreeTickets > 0;

    if (!event || !(promoCodeWithCharges || isRedeemingVisaFreeTicket)) {
      return null;
    }

    const promo = intl.formatMessage({
      id: 'event.checkout.promo',
    });
    const promoCodeDiscountValue = isRedeemingVisaFreeTicket
      ? ticketPrice
      : promoCodeDiscountAmount;

    return (
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={6}
      >
        <Box flexDirection="row" gap={1} alignItems="center">
          {isRedeemingVisaFreeTicket && <VisaLogo />}
          <Typography>
            {isRedeemingVisaFreeTicket ? promo.toLowerCase() : promo}
          </Typography>
        </Box>
        <Typography>{`-${currencyMainUnitsFormatter(
          promoCodeDiscountValue,
          currencySymbol
        )}`}</Typography>
      </Box>
    );
  };

  const BookingFeeSection = () => {
    if (!event || !bookingFeeMessage) {
      return null;
    }

    return (
      <Box
        data-qaid="event-page-checkout-widget-booking-fee-message"
        flexDirection="row"
        alignItems="center"
      >
        <Typography fontSize="fontSize75">{bookingFeeMessage}</Typography>
      </Box>
    );
  };

  const PromoCodeFormSection = () => {
    if (disabled || !allowPromoCode || visaPromoApplied) {
      return null;
    }

    return (
      <>
        <PromoCodeLink />
        <Divider color={colors.accent.primaryAccent.base} my={6} />
      </>
    );
  };

  const PriceBreakdownSection = () => {
    const checkoutSummaryCopy = intl.formatMessage({
      id:
        feeIncludedInDisplayPrice || !event.bookingFee
          ? 'event.checkout.total'
          : 'event.checkout.subTotal',
    });

    return (
      <>
        <Box flexDirection="column" gap={5}>
          <PromoCodeSummaryItem />
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={6}
          >
            <Typography
              fontSize="fontSize100"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {checkoutSummaryCopy}
            </Typography>
            <Typography fontSize="fontSize300" fontWeight="bold">
              {currencyMainUnitsFormatter(finalPrice, currencySymbol)}
            </Typography>
          </Box>
          {feeIncludedInDisplayPrice && (
            <Box
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Typography>
                {intl.formatMessage({
                  id: 'event.checkout.bookingFeeIncluded',
                })}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider my={6} color={colors.accent.primaryAccent.base} />
      </>
    );
  };

  function defaultShowVirtualPayment(): ShowVirtualPayment {
    return {
      showExpressApplePay: false,
      showExpressGooglePay: false,
    };
  }
  const { showExpressApplePay, showExpressGooglePay } =
    stripeVirtualWallet?.showVirtualPayment || defaultShowVirtualPayment();

  const isExpressCheckoutAllowed =
    !disabled &&
    finalPrice !== 0 &&
    !event.onPresale && // Presale events may only be purchased using the verified visa card
    !visaPromoApplied && // Visa promo may only be redeemed using the verified visa card
    (showExpressApplePay || showExpressGooglePay);

  const eventAttendeeTickets =
    eventAttendee && eventAttendee.visibleToUser && eventAttendee.tickets;
  const hasPurchasedMaxTickets =
    ((eventAttendeeTickets && eventAttendeeTickets.length) || 0) >=
    event.maxTicketsPerAttendee;

  const maxTickets = calcMaxTicketsCanPurchase(event, eventAttendee);

  const title = intl.formatMessage({
    id: isExpressCheckoutAllowed
      ? 'event.checkout.expressCheckoutTitle'
      : 'event.checkout.noExpressCheckoutTitle',
  });

  function ctaIntlId() {
    if (event.cancelled) {
      return 'event.eventCTA.canceled';
    } else if (hasPurchasedMaxTickets) {
      return 'event.eventCTA.maxTickets';
    } else if (event.isSoldOut && !!eventAttendee) {
      return 'event.eventCTA.soldOut';
    } else if (!event.isAvailable && !!eventAttendee) {
      return 'event.eventCTA.ticketSalesPaused';
    }

    return 'event.checkout.addToCart';
  }

  function onChangeNumberOfTickets(num: number) {
    if (num === 1 && visaPromoApplied) {
      setDisplaySingleTicketDeclineVisaFreeTicketConfirmationModal(true);
    } else {
      setNumberOfTickets(num);
    }
  }

  function confirmSingleTicketDeclineVisaFreeTicket() {
    setNumberOfTickets(1);
    setVisaPromoApplied && setVisaPromoApplied(false);
    setShouldDisplayVisaDisclaimer && setShouldDisplayVisaDisclaimer(false);
  }

  return (
    <Box data-qaid="event-page-checkout-widget" w="100%">
      <Box w="100%">
        <Box mb={8}>
          <Typography
            fontSize="fontSize500"
            fontWeight="medium"
            fontFamily="title"
          >
            {title}
          </Typography>
        </Box>
        <Box
          justifyContent="space-between"
          alignItems="center"
          w="100%"
          gap={4}
        >
          <Box
            justifyContent="left"
            flexDirection="column"
            gap={event.isEarlyBird ? 0 : 6}
          >
            <TicketPrice
              currencySymbol={currencySymbol}
              ticketPrice={ticketPrice}
              isEarlyBird={event.isEarlyBird}
              earlyBirdOriginalPrice={event.earlyBirdOriginalPrice}
            />
            <BookingFeeSection />
          </Box>
          <Box
            justifyContent="flex-end"
            alignItems="flex-end"
            alignContent="center"
            py={2}
          >
            <TicketQuantityStepper
              data-qaid="shopping-cart-ticket-quantity-stepper"
              numberOfTickets={numberOfTickets}
              setNumberOfTickets={onChangeNumberOfTickets}
              maxTickets={maxTickets}
              disabled={disabled}
              minTickets={minimumNumberOfTickets}
            />
          </Box>
        </Box>
        <Divider my={6} color={colors.accent.primaryAccent.base} />
        {PromoCodeFormSection()}
        {PriceBreakdownSection()}
        {isExpressCheckoutAllowed && (
          <VirtualWalletButtons
            finalPrice={finalPrice}
            event={event}
            eventAttendee={eventAttendee}
            stripeVirtualWallet={stripeVirtualWallet}
            isExpressCheckout={true}
            showApplePay={showExpressApplePay}
            showGooglePay={showExpressGooglePay}
          />
        )}
        <Box flexDirection="row">
          <ActionButton
            variant="primary"
            data-qaid="event-page-checkout-widget-cta-button"
            fullWidth={true}
            onClick={navigateToCheckout}
            disabled={hasPurchasedMaxTickets || disabled}
          >
            {intl.formatMessage({ id: ctaIntlId() })}
          </ActionButton>
        </Box>
        <Box flexDirection="row" justifyContent="flex-end">
          <TermsAndConditionsSummaryRow />
        </Box>
        {!loggedIn && isExpressCheckoutAllowed && (
          <Box mt={6}>
            <Checkbox
              onChange={() => {
                setOptinGuestMarketing(!optinGuestMarketing);
              }}
              checked={optinGuestMarketing}
              id="marketingOptIn"
              name="marketingOptIn"
              data-qaid="email-marketing-input"
            >
              <Typography fontSize="fontSize75" ml={2}>
                {intl.formatMessage({
                  id: 'event.checkout.marketingOptInCaption',
                })}
              </Typography>
            </Checkbox>
          </Box>
        )}
      </Box>
      {displayAdditionalPurchaseConfirmationModal && (
        <AdditionalPurchaseConfirmationModal
          isShowing={displayAdditionalPurchaseConfirmationModal}
          setIsShowing={setDisplayAdditionalPurchaseConfirmationModal}
          confirmationAction={navigateToCheckout}
        />
      )}
      {visaPromoApplied && (
        <VisaBenefitRemovalConfirmationModal
          data-qaid="visa-benefit-removal-confirmation-modal"
          isOpen={displaySingleTicketDeclineVisaFreeTicketConfirmationModal}
          setIsOpen={
            setDisplaySingleTicketDeclineVisaFreeTicketConfirmationModal
          }
          confirmationAction={confirmSingleTicketDeclineVisaFreeTicket}
          portalRootId="sofar-portal-root-2"
        />
      )}
    </Box>
  );
};
